import { useEffect, useState } from 'react';
import { apiClient } from '../lib/api';

/**
 * Resolve a productId → isPeptide lookup for a set of products, used to render
 * the "filled by" pharmacy badge on order views whose line items only snapshot
 * productId. Fetches via /products/by-ids (any authenticated role) and caches
 * by the sorted id set so re-renders don't refetch.
 */
export function useProductPeptideMap(productIds: string[]): Record<string, boolean> {
  const [map, setMap] = useState<Record<string, boolean>>({});

  // Stable key: unique, sorted ids. Empty string when there's nothing to fetch.
  const key = Array.from(new Set(productIds.map(String).filter(Boolean))).sort().join(',');

  useEffect(() => {
    if (!key) return;
    const ids = key.split(',');
    let cancelled = false;

    (async () => {
      try {
        const res = await apiClient.getProductsByIds(ids);
        if (cancelled || !res.success || !res.data) return;
        const next: Record<string, boolean> = {};
        for (const p of res.data.products) next[String(p._id)] = !!p.isPeptide;
        // Products not returned (e.g. inactive) resolve to non-peptide so the
        // badge still renders rather than hanging on "unresolved".
        for (const id of ids) if (!(id in next)) next[id] = false;
        setMap(next);
      } catch {
        // Best-effort: leave the map empty; badges simply won't render.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [key]);

  return map;
}
